import { useState, useCallback } from 'react';

import type { EmpresaPlataforma } from '@/mocks/backoffice/empresas';

import EmpresaPlataformaConfig from './EmpresaPlataformaConfig';
import EmpresaCampanhasConfig from './EmpresaCampanhasConfig';
import EmpresaPremiadosConfig from './EmpresaPremiadosConfig';

// Configurações "default" da empresa — as campanhas desta empresa herdam ou sobrescrevem
const configsDefaultEmpresa: Record<string, { valor: string; descricao: string; tipo: string; categoria: string }> = {
  taxa_processamento_pix: { valor: '2.5', descricao: 'Taxa percentual cobrada sobre cada resgate PIX', tipo: 'porcentagem', categoria: 'taxas' },
  taxa_compra_pontos: { valor: '5.0', descricao: 'Taxa percentual cobrada sobre compra de pontos', tipo: 'porcentagem', categoria: 'taxas' },
  taxa_transferencia_interna: { valor: '1.0', descricao: 'Taxa percentual sobre transferência entre participantes', tipo: 'porcentagem', categoria: 'taxas' },
  limite_resgate_diario: { valor: '1000', descricao: 'Valor máximo de resgate PIX por participante por dia (R$)', tipo: 'numero', categoria: 'limites' },
  limite_resgate_mensal: { valor: '5000', descricao: 'Valor máximo de resgate PIX por participante por mês (R$)', tipo: 'numero', categoria: 'limites' },
  limite_transferencia_diaria: { valor: '3000', descricao: 'Valor máximo de transferência entre participantes por dia (R$)', tipo: 'numero', categoria: 'limites' },
  limite_pontos_campanha: { valor: '500000', descricao: 'Quantidade máxima de pontos por campanha', tipo: 'numero', categoria: 'limites' },
  pontos_por_real_venda: { valor: '10', descricao: 'Pontos ganhos por real em venda (padrão)', tipo: 'numero', categoria: 'regras' },
  multiplicador_bonificacao: { valor: '1.5', descricao: 'Multiplicador de pontos em bonificações', tipo: 'numero', categoria: 'regras' },
  prazo_aprovacao_saque: { valor: '48', descricao: 'Prazo máximo em horas para aprovação de saques', tipo: 'numero', categoria: 'regras' },
  ativo_verificacao_kyc: { valor: 'true', descricao: 'Ativar verificação de identidade (KYC) para novos cadastros', tipo: 'boolean', categoria: 'regras' },
  email_notificacoes: { valor: 'notificacoes@getpoints.com.br', descricao: 'Email remetente para notificações da empresa', tipo: 'texto', categoria: 'geral' },
  moeda_padrao: { valor: 'BRL', descricao: 'Moeda padrão da empresa', tipo: 'texto', categoria: 'geral' },
  ativo_moderacao_automatica: { valor: 'true', descricao: 'Ativar moderação automática de conteúdo no feed social', tipo: 'boolean', categoria: 'geral' },
};

type AbaConfig = 'plataforma' | 'campanhas' | 'premiados';

const abas: { id: AbaConfig; label: string; icon: string; descricao: string }[] = [
  { id: 'plataforma', label: 'Plataforma', icon: 'ri-global-line', descricao: 'Configurações base da empresa (herdadas pelas campanhas)' },
  { id: 'campanhas', label: 'Campanhas', icon: 'ri-megaphone-line', descricao: 'Parametrização por campanha desta empresa' },
  { id: 'premiados', label: 'Premiados', icon: 'ri-user-star-line', descricao: 'Configuração individual por campanha de cada participante' },
];

interface EmpresaConfigProps {
  empresa: EmpresaPlataforma;
}

export default function EmpresaConfig({ empresa }: EmpresaConfigProps) {
  const [abaAtiva, setAbaAtiva] = useState<AbaConfig>('plataforma');
  const [configsEmpresa, setConfigsEmpresa] = useState(configsDefaultEmpresa);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const mostrarToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleUpdateEmpresa = (chave: string, valor: string) => {
    setConfigsEmpresa((prev) => ({
      ...prev,
      [chave]: { ...prev[chave], valor },
    }));
    mostrarToast('Configuração da empresa salva com sucesso', 'success');
  };

  const abaAtual = abas.find((a) => a.id === abaAtiva);

  return (
    <div className="space-y-5">
      {/* Sub-abas */}
      <div className="bg-white rounded-xl border border-gray-100 p-1.5">
        <div className="grid grid-cols-3 gap-1">
          {abas.map((aba) => (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                abaAtiva === aba.id
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-transparent'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${aba.icon} ${abaAtiva === aba.id ? 'text-emerald-600' : 'text-gray-400'}`}></i>
              </div>
              <span className="hidden sm:inline">{aba.label}</span>
              <span className="sm:hidden">{aba.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Info bar */}
      <div className="flex items-center space-x-3 text-xs text-gray-400">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`${abaAtual?.icon} text-sm`}></i>
        </div>
        <span>{abaAtual?.descricao}</span>
      </div>

      {/* Conteúdo */}
      {abaAtiva === 'plataforma' && (
        <EmpresaPlataformaConfig
          empresa={empresa}
          configs={configsEmpresa}
          onUpdate={handleUpdateEmpresa}
        />
      )}
      {abaAtiva === 'campanhas' && (
        <EmpresaCampanhasConfig
          empresa={empresa}
          onShowToast={mostrarToast}
        />
      )}
      {abaAtiva === 'premiados' && (
        <EmpresaPremiadosConfig
          empresa={empresa}
          onShowToast={mostrarToast}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-${toast.type === 'success' ? 'check-line' : 'error-warning-line'} text-sm`}></i>
          </div>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}